import { randomId } from "@codeshare/protocol";
import type { Awareness } from "y-protocols/awareness";
import type { Workspace } from "./workspace";

/**
 * A program run whose terminal is shared with the room (docs/PRD.md, "Terminal streaming").
 * Stored as a plain value under `sessions[id]`; only its host writes it, anyone may delete it once
 * the host is gone.
 */
export interface RunSessionInfo {
  id: string;
  /** The host's room peer id: guests receive the `term:<id>` channel from this peer. */
  hostPeerId: string;
  /** The host's awareness client id, for liveness (its awareness state disappears when it leaves). */
  hostClient: number;
  hostName: string;
  /** The entry file (path, and id when it's a workspace file). */
  file: string;
  fileId?: string;
  runnerId: string;
  status: "running" | "exited";
  exitCode?: number;
  startedAt: number;
  endedAt?: number;
  /** Whether guests may type into the program. Off until the host turns it on. */
  allowInput: boolean;
}

export type NewRunSession = Pick<RunSessionInfo, "hostPeerId" | "hostName" | "file" | "runnerId"> &
  Partial<Pick<RunSessionInfo, "fileId">>;

function parse(id: string, value: unknown): RunSessionInfo | undefined {
  if (!value || typeof value !== "object") return undefined;
  const v = value as Record<string, unknown>;
  if (
    typeof v.hostPeerId !== "string" ||
    typeof v.hostClient !== "number" ||
    typeof v.hostName !== "string" ||
    typeof v.file !== "string" ||
    typeof v.runnerId !== "string" ||
    (v.status !== "running" && v.status !== "exited") ||
    typeof v.startedAt !== "number"
  ) {
    return undefined;
  }
  const info: RunSessionInfo = {
    id,
    hostPeerId: v.hostPeerId,
    hostClient: v.hostClient,
    hostName: v.hostName,
    file: v.file,
    runnerId: v.runnerId,
    status: v.status,
    startedAt: v.startedAt,
    allowInput: v.allowInput === true,
  };
  if (typeof v.fileId === "string") info.fileId = v.fileId;
  if (typeof v.exitCode === "number") info.exitCode = v.exitCode;
  if (typeof v.endedAt === "number") info.endedAt = v.endedAt;
  return info;
}

/**
 * The room's run sessions. Lists only sessions whose host is still here (present in awareness),
 * and removes the others: right away when a host's awareness state goes away, and for sessions
 * left over from earlier visits, once their host has been absent for two sweeps in a row.
 */
export class RunSessions {
  readonly #workspace: Workspace;
  readonly #awareness: Awareness;
  readonly #listeners = new Set<() => void>();
  readonly #cleanup: (() => void)[] = [];
  #absent = new Set<string>();

  constructor(options: { workspace: Workspace; awareness: Awareness; sweepMs?: number }) {
    this.#workspace = options.workspace;
    this.#awareness = options.awareness;
    const onSessions = () => this.#emit();
    this.#workspace.sessions.observe(onSessions);
    this.#cleanup.push(() => this.#workspace.sessions.unobserve(onSessions));

    const onAwareness = ({ added, removed }: { added: number[]; removed: number[] }) => {
      if (removed.length > 0) this.#removeHostedBy(new Set(removed));
      if (added.length > 0 || removed.length > 0) this.#emit();
    };
    this.#awareness.on("change", onAwareness);
    this.#cleanup.push(() => this.#awareness.off("change", onAwareness));

    const timer = setInterval(() => this.sweep(), options.sweepMs ?? 15_000);
    this.#cleanup.push(() => clearInterval(timer));
  }

  /** Sessions whose host is here (including ours), oldest first. */
  list(): RunSessionInfo[] {
    return this.#all()
      .filter((s) => this.#present(s.hostClient))
      .sort((a, b) => a.startedAt - b.startedAt);
  }

  get(id: string): RunSessionInfo | undefined {
    const info = parse(id, this.#workspace.sessions.get(id));
    return info && this.#present(info.hostClient) ? info : undefined;
  }

  /** Starts a session hosted by this client, replacing its earlier ones. Returns its id. */
  start(session: NewRunSession): string {
    const id = randomId();
    const self = this.#awareness.clientID;
    this.#workspace.doc.transact(() => {
      for (const old of this.#all()) {
        if (old.hostClient === self) this.#workspace.sessions.delete(old.id);
      }
      const { id: _id, ...value } = {
        ...session,
        id,
        hostClient: self,
        status: "running",
        startedAt: Date.now(),
        allowInput: false,
      } satisfies RunSessionInfo;
      this.#workspace.sessions.set(id, value);
    });
    return id;
  }

  /** Updates a session this client hosts. */
  update(
    id: string,
    patch: Partial<Pick<RunSessionInfo, "status" | "exitCode" | "endedAt" | "allowInput">>,
  ): void {
    const current = parse(id, this.#workspace.sessions.get(id));
    if (!current || current.hostClient !== this.#awareness.clientID) return;
    const { id: _id, ...value } = { ...current, ...patch };
    this.#workspace.sessions.set(id, value);
  }

  remove(id: string): void {
    this.#workspace.sessions.delete(id);
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  /** Removes sessions whose host was absent at the previous sweep and still is. */
  sweep(): void {
    const absent = new Set(
      this.#all()
        .filter((s) => !this.#present(s.hostClient))
        .map((s) => s.id),
    );
    const gone = [...absent].filter((id) => this.#absent.has(id));
    this.#absent = absent;
    if (gone.length > 0 && this.#workspace.canWrite) {
      this.#workspace.doc.transact(() => {
        for (const id of gone) this.#workspace.sessions.delete(id);
      });
    }
  }

  destroy(): void {
    for (const fn of this.#cleanup.splice(0)) fn();
    this.#listeners.clear();
  }

  #all(): RunSessionInfo[] {
    const out: RunSessionInfo[] = [];
    for (const [id, value] of this.#workspace.sessions.entries()) {
      const info = parse(id, value);
      if (info) out.push(info);
    }
    return out;
  }

  #present(client: number): boolean {
    return client === this.#awareness.clientID || this.#awareness.getStates().has(client);
  }

  #removeHostedBy(clients: Set<number>): void {
    const doomed = this.#all().filter((s) => clients.has(s.hostClient));
    if (doomed.length === 0 || !this.#workspace.canWrite) return;
    this.#workspace.doc.transact(() => {
      for (const s of doomed) this.#workspace.sessions.delete(s.id);
    });
  }

  #emit(): void {
    for (const listener of this.#listeners) listener();
  }
}
